import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { IProductData } from '../components/TagsModal/TagsModal';
import * as Hub from '~/ogm-hub';
import { saveMetaProduct } from './MetaProductsSlice';
import {
	getMetaProductSupplierCode,
	getMetaProductBlueprintId,
	getMetaProductIsDssiSite,
} from './MetaProductPage/MetaProductPageSlice';
import { type IAppState } from '~/appstate';
import { UmSelectionMode } from '~/domain';
import { toTitleCase } from '~/helpers/String';
import {
	buildTagSearchRequest,
	generalGroupsFromChecked,
	mergeDroppedTag,
	singleTypeOrGroup,
	tagLinksStillSelected,
	type DraggedTag,
} from '~/helpers/tagLinks';
import * as LocalStorage from '~/helpers/LocalStorage';
import { toastr } from '~/helpers/Toastr';

export interface TagOption {
	value: string;
	label: string;
	excluded: boolean;
	/** Rearrange: tag picked from a product row (not the source master-item chip). */
	fromProduct?: boolean;
}

const selectionStillHasTags = (selections: ITagsModalState['selections']) =>
	selections.selectedCategory ||
	selections.selectedPrimary ||
	selections.selectedPackaging ||
	(selections.selectedGeneral && selections.selectedGeneral.length > 0);

function appendResolvedTagOptions(
	resolvedTags: Hub.Tag[],
	members: TagOption[],
	tagType: Hub.TagType
): Hub.Tag[] {
	const nextResolved = [...resolvedTags];
	members.forEach((member) => {
		const tagId = parseInt(member.value, 10);
		if (nextResolved.some((tag) => tag.id === tagId)) {
			return;
		}
		nextResolved.push({
			id: tagId,
			name: toTitleCase(member.label),
			tagType,
		} as Hub.Tag);
	});
	return nextResolved;
}

export interface ITagsModalState {
	selections: {
		productNumber: string;
		selectedCategory: TagOption | null;
		selectedPrimary: TagOption | null;
		selectedPackaging: TagOption | null;
		selectedGeneral: TagOption[];
		showProductTags: boolean;
	};
	data: {
		categoryOptions: TagOption[];
		primaryOptions: TagOption[];
		packagingOptions: TagOption[];
		generalOptions: TagOption[];
		previewProducts: IProductData[];
		includedTags: Hub.Tag[];
		excludedTags: Hub.Tag[];
		allTags: Hub.Tag[];
	};
	loadingStates: {
		isLoading: boolean;
		isApplying: boolean;
		isFetchingTags: boolean;
		isFetchingProducts: boolean;
		productsHaveBeenFetched: boolean;
		isApplyingProducts: boolean;
	};
	errors: {
		loadError: string | null;
		applyError: string | null;
		fetchTagsError: string | null;
		fetchProductsError: string | null;
	};
	currentMasterItemId: string | null;
	existingProductKeys: string[];
	workingTagLinks: Hub.MetaProductTagLink[] | null;
	workingResolvedTags: Hub.Tag[] | null;
	tagSearch: {
		category: { options: TagOption[]; loading: boolean };
		primary: { options: TagOption[]; loading: boolean };
		packaging: { options: TagOption[]; loading: boolean };
		general: { options: TagOption[]; loading: boolean };
	};
}

const initialState: ITagsModalState = {
	selections: {
		productNumber: '',
		selectedCategory: null,
		selectedPrimary: null,
		selectedPackaging: null,
		selectedGeneral: [],
		showProductTags: LocalStorage.getShowProductTags() ?? false,
	},
	data: {
		categoryOptions: [],
		primaryOptions: [],
		packagingOptions: [],
		generalOptions: [],
		previewProducts: [],
		includedTags: [],
		excludedTags: [],
		allTags: [],
	},
	loadingStates: {
		isLoading: false,
		isApplying: false,
		isFetchingTags: false,
		isFetchingProducts: false,
		productsHaveBeenFetched: false,
		isApplyingProducts: false,
	},
	errors: {
		loadError: null,
		applyError: null,
		fetchTagsError: null,
		fetchProductsError: null,
	},
	currentMasterItemId: null,
	existingProductKeys: [],
	workingTagLinks: null,
	workingResolvedTags: null,
	tagSearch: {
		category: { options: [], loading: false },
		primary: { options: [], loading: false },
		packaging: { options: [], loading: false },
		general: { options: [], loading: false },
	},
};

export const searchTags = (args: {
	tagType: 'category' | 'primary' | 'packaging' | 'general';
	searchTerm: string;
}) => {
	return async (dispatch) => {
		const hubTagType = {
			category: Hub.TagType.Category,
			primary: Hub.TagType.Primary,
			packaging: Hub.TagType.Packaging,
			general: Hub.TagType.General,
		}[args.tagType];

		dispatch(tagsModalSlice.actions.tagSearchLoading(args.tagType));

		try {
			const response = await Hub.TagController.SearchTags({
				searchTerm: args.searchTerm,
				tagType: [hubTagType],
				orderBy: Hub.TagOrder.Alphabetical,
				descending: false,
				skip: 0,
				take: 50,
				includeEmbedding: false,
			});

			if (!response.ok) {
				dispatch(tagsModalSlice.actions.tagSearchError(args.tagType));
				return;
			}

			const options: TagOption[] = response.value.tags.map((tag) => ({
				value: tag.id.toString(),
				label: toTitleCase(tag.name),
				excluded: false,
			}));

			dispatch(
				tagsModalSlice.actions.tagSearchLoaded({
					tagType: args.tagType,
					options,
				})
			);
		} catch {
			dispatch(tagsModalSlice.actions.tagSearchError(args.tagType));
		}
	};
};

export const fetchProductsByTags = createAsyncThunk(
	'tagsModal/fetchProductsByTags',
	async (
		params: {
			selectedCategory?: TagOption | null;
			selectedPrimary?: TagOption | null;
			selectedPackaging?: TagOption | null;
			selectedGeneral?: TagOption[];
		},
		{ rejectWithValue, getState }
	) => {
		const state = getState() as IAppState;
		const supplierCode = getMetaProductSupplierCode(state);
		const blueprintId = getMetaProductBlueprintId(state);
		const isDSSISite = getMetaProductIsDssiSite(state);
		const eachesOnly =
			state.metaProductPage.page.metaProduct?.umSelectionModeId ===
			UmSelectionMode.EachOnly;

		const request = buildTagSearchRequest(
			supplierCode,
			{
				category: params.selectedCategory,
				primary: params.selectedPrimary,
				packaging: params.selectedPackaging,
				general: params.selectedGeneral,
			},
			state.tagsModal.workingTagLinks ??
				state.metaProductPage.page.tagLinks ??
				[]
		);

		let response = await Hub.DssiProductController.SearchProducts(
			request,
			blueprintId,
			isDSSISite,
			eachesOnly
		);

		if (!response.ok) {
			toastr.warning({
				title: 'Product Search Failed',
				body: 'Initial product search failed, retrying...',
			});
			response = await Hub.DssiProductController.SearchProducts(
				request,
				blueprintId,
				isDSSISite,
				eachesOnly
			);
		}

		if (!response.ok) {
			toastr.error({
				title: 'Product Search Error',
				body: 'Failed to search for products. Please try again.',
			});
			return rejectWithValue('Failed to fetch products by tags');
		}

		const responseValue = response.value;
		const productsWithTags = responseValue?.productsWithTags || [];

		const products: IProductData[] = productsWithTags.map((pwt) => ({
			id: pwt.product.id.toString(),
			productNumber: pwt.product.supplierProductNumber,
			supplierProductNumber: pwt.product.supplierProductNumber,
			corpSupplierCode: pwt.product.corpSupplierCode,
			productName: pwt.product.description,
			description: pwt.product.description,
			extendedDescription: pwt.product.extendedDescription,
			packSize: pwt.product.packSize,
			tags: pwt.tags,
			coveredDistributionCenters:
				pwt.product.coveredDistributionCenters?.map((dc) => ({
					distributionCenter: dc.distributionCenter,
					unitMeasureCodes: dc.unitMeasureCodes || [],
				})) || [],
			remoteStockDcs:
				pwt.product.remoteStockDcs?.map((dc) => ({
					distributionCenter: dc.distributionCenter,
					unitMeasureCodes: dc.unitMeasureCodes || [],
				})) || [],
			demandStockDcs:
				pwt.product.demandStockDcs?.map((dc) => ({
					distributionCenter: dc.distributionCenter,
					unitMeasureCodes: dc.unitMeasureCodes || [],
				})) || [],
			nonstockDcs:
				pwt.product.nonstockDcs?.map((dc) => ({
					distributionCenter: dc.distributionCenter,
					unitMeasureCodes: dc.unitMeasureCodes || [],
				})) || [],
		}));

		return products;
	}
);

export const handleTagSelectionAndFetch = createAsyncThunk(
	'tagsModal/handleTagSelectionAndFetch',
	async (
		params: {
			actionType: 'category' | 'primary' | 'packaging' | 'general';
			value: TagOption | TagOption[] | null;
		},
		{ dispatch, getState }
	) => {
		let pendingGroup: {
			tagId: number;
			excluded: boolean;
			orTagIds: number[];
			members: TagOption[];
			tagType: Hub.TagType;
		} | null = null;
		let pendingGeneral: {
			previousPrimaryIds: number[];
			links: Hub.MetaProductTagLink[];
			members: TagOption[];
		} | null = null;

		const setSingleTypeSelection = (
			actionType: 'category' | 'primary' | 'packaging',
			value: TagOption | TagOption[] | null
		) => {
			const update = {
				category: tagsModalSlice.actions.updateSelectedCategory,
				primary: tagsModalSlice.actions.updateSelectedPrimary,
				packaging: tagsModalSlice.actions.updateSelectedPackaging,
			}[actionType];
			const tagType = {
				category: Hub.TagType.Category,
				primary: Hub.TagType.Primary,
				packaging: Hub.TagType.Packaging,
			}[actionType];
			const selectionKey = {
				category: 'selectedCategory',
				primary: 'selectedPrimary',
				packaging: 'selectedPackaging',
			}[actionType] as
				| 'selectedCategory'
				| 'selectedPrimary'
				| 'selectedPackaging';

			if (!Array.isArray(value)) {
				dispatch(update((value as TagOption) || null));
				return;
			}

			const current = (getState() as IAppState).tagsModal.selections[
				selectionKey
			];
			const group = singleTypeOrGroup(value, current);
			dispatch(update(group?.primary ?? null));
			if (!group) {
				return;
			}

			pendingGroup = {
				tagId: parseInt(group.primary.value, 10),
				excluded: group.primary.excluded,
				orTagIds: group.orTagIds,
				members: value,
				tagType,
			};
		};

		switch (params.actionType) {
			case 'category':
			case 'primary':
			case 'packaging':
				setSingleTypeSelection(params.actionType, params.value);
				break;
			case 'general':
				if (Array.isArray(params.value)) {
					const appState = getState() as IAppState;
					const currentGeneral =
						appState.tagsModal.selections.selectedGeneral ?? [];
					const groups = generalGroupsFromChecked(
						params.value,
						currentGeneral,
						appState.tagsModal.workingTagLinks ??
							appState.metaProductPage.page.tagLinks ??
							[]
					);
					pendingGeneral = {
						previousPrimaryIds: currentGeneral.map((tag) =>
							parseInt(tag.value, 10)
						),
						links: groups.links,
						members: params.value,
					};
					dispatch(
						tagsModalSlice.actions.updateSelectedGeneral(groups.primaries)
					);
				} else if (params.value === null) {
					dispatch(tagsModalSlice.actions.updateSelectedGeneral([]));
				} else {
					const current =
						(getState() as IAppState).tagsModal.selections.selectedGeneral ??
						[];
					const single = params.value as TagOption;
					const exists = current.some((tag) => tag.value === single.value);

					dispatch(
						tagsModalSlice.actions.updateSelectedGeneral(
							exists
								? current.map((tag) =>
										tag.value === single.value
											? { ...tag, excluded: single.excluded }
											: tag
								  )
								: [...current, single]
						)
					);
				}
				break;
		}

		const state = getState() as IAppState;
		const { selections } = state.tagsModal;
		const selectedTagIds = [
			selections.selectedCategory,
			selections.selectedPrimary,
			selections.selectedPackaging,
			...(selections.selectedGeneral ?? []),
		]
			.filter((option): option is TagOption => option != null)
			.map((option) => parseInt(option.value, 10));
		const currentLinks =
			state.tagsModal.workingTagLinks ??
			state.metaProductPage.page.tagLinks ??
			[];
		let nextLinks = tagLinksStillSelected(currentLinks, selectedTagIds);
		const nextResolved = [
			...(state.tagsModal.workingResolvedTags ??
				state.metaProductPage.page.resolvedTags ??
				[]),
		];

		let nextResolvedTags = nextResolved;

		if (pendingGroup) {
			const group = pendingGroup;
			nextLinks = nextLinks.filter((link) => link.tagId !== group.tagId);
			nextLinks.push({
				tagId: group.tagId,
				excluded: group.excluded,
				orTagIds: group.orTagIds,
			});
			nextResolvedTags = appendResolvedTagOptions(
				nextResolvedTags,
				group.members,
				group.tagType
			);
		}

		if (pendingGeneral) {
			const replaced = new Set(pendingGeneral.previousPrimaryIds);
			pendingGeneral.links.forEach((link) => replaced.add(link.tagId));
			nextLinks = nextLinks.filter((link) => !replaced.has(link.tagId));
			nextLinks.push(...pendingGeneral.links);
			nextResolvedTags = appendResolvedTagOptions(
				nextResolvedTags,
				pendingGeneral.members,
				Hub.TagType.General
			);
		}

		dispatch(
			tagsModalSlice.actions.setWorkingTags({
				tagLinks: nextLinks,
				resolvedTags: nextResolvedTags,
			})
		);

		if (!selectionStillHasTags(selections)) {
			dispatch(tagsModalSlice.actions.setPreviewProducts([]));
		} else {
			dispatch(
				fetchProductsByTags({
					selectedCategory: selections.selectedCategory,
					selectedPrimary: selections.selectedPrimary,
					selectedPackaging: selections.selectedPackaging,
					selectedGeneral: selections.selectedGeneral,
				})
			);
		}
	}
);

export const formOrGroup = (params: {
	targetTagId: number;
	targetExcluded: boolean;
	dropped: DraggedTag;
}) => {
	return async (dispatch, getState) => {
		const state = getState() as IAppState;
		const currentLinks =
			state.tagsModal.workingTagLinks ??
			state.metaProductPage.page.tagLinks ??
			[];
		if (params.targetExcluded || params.dropped.excluded) {
			return;
		}

		const nextLinks = mergeDroppedTag(
			currentLinks,
			params.targetTagId,
			params.targetExcluded,
			params.dropped.tagId,
			params.dropped.orTagIds
		);
		if (!nextLinks) {
			return;
		}

		const resolved =
			state.tagsModal.workingResolvedTags ??
			state.metaProductPage.page.resolvedTags ??
			[];
		const droppedTag = {
			id: params.dropped.tagId,
			name: toTitleCase(params.dropped.name),
			tagType: params.dropped.tagType,
		} as Hub.Tag;
		const nextResolved = resolved.some((tag) => tag.id === droppedTag.id)
			? resolved
			: [...resolved, droppedTag];

		dispatch(
			tagsModalSlice.actions.setWorkingTags({
				tagLinks: nextLinks,
				resolvedTags: nextResolved,
			})
		);

		if (params.dropped.fromSelection) {
			const droppedValue = params.dropped.tagId.toString();
			const selections = (getState() as IAppState).tagsModal.selections;
			switch (params.dropped.tagType) {
				case Hub.TagType.Category:
					if (selections.selectedCategory?.value === droppedValue) {
						dispatch(tagsModalSlice.actions.updateSelectedCategory(null));
					}
					break;
				case Hub.TagType.Primary:
					if (selections.selectedPrimary?.value === droppedValue) {
						dispatch(tagsModalSlice.actions.updateSelectedPrimary(null));
					}
					break;
				case Hub.TagType.Packaging:
					if (selections.selectedPackaging?.value === droppedValue) {
						dispatch(tagsModalSlice.actions.updateSelectedPackaging(null));
					}
					break;
				case Hub.TagType.General:
					dispatch(
						tagsModalSlice.actions.updateSelectedGeneral(
							(selections.selectedGeneral ?? []).filter(
								(tag) => tag.value !== droppedValue
							)
						)
					);
					break;
			}
		}

		const selections = (getState() as IAppState).tagsModal.selections;
		if (!selectionStillHasTags(selections)) {
			dispatch(tagsModalSlice.actions.setPreviewProducts([]));
			return;
		}

		dispatch(
			fetchProductsByTags({
				selectedCategory: selections.selectedCategory,
				selectedPrimary: selections.selectedPrimary,
				selectedPackaging: selections.selectedPackaging,
				selectedGeneral: selections.selectedGeneral,
			})
		);
	};
};

export const GetTagsFromProductNumber = createAsyncThunk(
	'tagsModal/getTagsFromProductNumber',
	async (
		params: {
			productNumber: string;
		},
		{ rejectWithValue, getState }
	) => {
		const state = getState() as IAppState;
		const supplierCode = getMetaProductSupplierCode(state);
		const blueprintId = getMetaProductBlueprintId(state);
		const isDSSISite = getMetaProductIsDssiSite(state);

		const request: Hub.GetProductTagLinksRequest = {
			productIds: [],
			supplierCodeAndProductNumbers: [
				{
					supplierCode: supplierCode,
					supplierProductNumber: params.productNumber,
				},
			],
		};

		const response = await Hub.ProductTagLinkController.GetProductTagLinks(
			request,
			blueprintId,
			isDSSISite
		);

		if (!response.ok) {
			let errorMessage = 'Cannot find product or product does not have tags';
			if (response.kind === 'failure' && response.errors?.length > 0) {
				errorMessage = response.errors[0];
			}
			return rejectWithValue(errorMessage);
		}

		const tags = response.value;
		const categoryTag = tags.find(
			(tag) => tag.tagType === Hub.TagType.Category
		);
		const primaryTag = tags.find((tag) => tag.tagType === Hub.TagType.Primary);
		const packagingTag = tags.find(
			(tag) => tag.tagType === Hub.TagType.Packaging
		);
		const generalTags = tags.filter(
			(tag) => tag.tagType === Hub.TagType.General
		);

		const selectedCategory: TagOption | null = categoryTag
			? {
					value: categoryTag.id.toString(),
					label: toTitleCase(categoryTag.name),
					excluded: false,
			  }
			: null;

		const selectedPrimary: TagOption | null = primaryTag
			? {
					value: primaryTag.id.toString(),
					label: toTitleCase(primaryTag.name),
					excluded: false,
			  }
			: null;

		const selectedPackaging: TagOption | null = packagingTag
			? {
					value: packagingTag.id.toString(),
					label: toTitleCase(packagingTag.name),
					excluded: false,
			  }
			: null;

		const selectedGeneral: TagOption[] = generalTags.map((tag) => ({
			value: tag.id.toString(),
			label: toTitleCase(tag.name),
			excluded: false,
		}));

		const result = {
			productNumber: params.productNumber,
			selectedCategory,
			selectedPrimary,
			selectedPackaging,
			selectedGeneral,
		};

		return result;
	}
);

export const applyTagsFromProduct = createAsyncThunk(
	'tagsModal/applyTagsFromProduct',
	async (_, { dispatch, getState }) => {
		const state = getState() as IAppState;
		const productNumber = state.tagsModal.selections.productNumber;
		const result = await dispatch(GetTagsFromProductNumber({ productNumber }));

		if (GetTagsFromProductNumber.fulfilled.match(result)) {
			const {
				selectedCategory,
				selectedPrimary,
				selectedPackaging,
				selectedGeneral,
			} = result.payload;

			const hasAnyTags =
				selectedCategory ||
				selectedPrimary ||
				selectedPackaging ||
				(selectedGeneral && selectedGeneral.length > 0);

			if (hasAnyTags) {
				await dispatch(
					fetchProductsByTags({
						selectedCategory,
						selectedPrimary,
						selectedPackaging,
						selectedGeneral,
					})
				);
			} else {
				dispatch(tagsModalSlice.actions.setPreviewProducts([]));
			}
		}

		return result;
	}
);

export const tagsModalSlice = createSlice({
	name: 'tagsModal',
	initialState,
	reducers: {
		resetModal: (state) => {
			state.selections = {
				productNumber: '',
				selectedCategory: null,
				selectedPrimary: null,
				selectedPackaging: null,
				selectedGeneral: [],
				showProductTags: LocalStorage.getShowProductTags() ?? false,
			};
			state.errors = {
				loadError: null,
				applyError: null,
				fetchTagsError: null,
				fetchProductsError: null,
			};
			state.loadingStates.productsHaveBeenFetched = false;
			state.loadingStates.isFetchingProducts = false;
			state.loadingStates.isApplying = false;
			state.data.previewProducts = [];
			state.currentMasterItemId = null;
			state.existingProductKeys = [];
			state.workingTagLinks = null;
			state.workingResolvedTags = null;
			state.tagSearch = {
				category: { options: [], loading: false },
				primary: { options: [], loading: false },
				packaging: { options: [], loading: false },
				general: { options: [], loading: false },
			};
			if (state.loadingStates.isFetchingTags) {
				state.data.categoryOptions = [];
				state.data.primaryOptions = [];
				state.data.packagingOptions = [];
				state.data.generalOptions = [];
				state.data.includedTags = [];
				state.data.excludedTags = [];
				state.data.allTags = [];
			}
		},
		openModalForMasterItem: (
			state,
			action: PayloadAction<{
				masterItemId: string | number | null;
				existingProductKeys: string[];
			}>
		) => {
			const { masterItemId, existingProductKeys } = action.payload;
			if (state.currentMasterItemId !== masterItemId?.toString()) {
				state.selections = {
					productNumber: '',
					selectedCategory: null,
					selectedPrimary: null,
					selectedPackaging: null,
					selectedGeneral: [],
					showProductTags: LocalStorage.getShowProductTags() ?? false,
				};
				state.data.previewProducts = [];
				state.loadingStates.productsHaveBeenFetched = false;
				state.loadingStates.isFetchingProducts = false;
				state.currentMasterItemId = masterItemId?.toString() ?? null;
				state.workingTagLinks = null;
				state.workingResolvedTags = null;
			}
			state.existingProductKeys = existingProductKeys;
		},
		setWorkingTags: (
			state,
			action: PayloadAction<{
				tagLinks: Hub.MetaProductTagLink[];
				resolvedTags: Hub.Tag[];
			}>
		) => {
			state.workingTagLinks = action.payload.tagLinks.map((link) => ({
				tagId: link.tagId,
				excluded: link.excluded,
				orTagIds: [...(link.orTagIds ?? [])],
			}));
			state.workingResolvedTags = action.payload.resolvedTags.map((tag) => ({
				...tag,
			}));
		},
		updateProductNumber: (state, action) => {
			state.selections.productNumber = action.payload;
			state.errors.loadError = null;
		},
		updateSelectedCategory: (state, action) => {
			state.selections.selectedCategory = action.payload;
		},
		updateSelectedPrimary: (state, action) => {
			state.selections.selectedPrimary = action.payload;
		},
		updateSelectedPackaging: (state, action) => {
			state.selections.selectedPackaging = action.payload;
		},
		updateSelectedGeneral: (state, action: PayloadAction<TagOption[]>) => {
			state.selections.selectedGeneral = action.payload;
		},
		toggleShowProductTags: (state) => {
			state.selections.showProductTags = !state.selections.showProductTags;
			LocalStorage.setShowProductTags(state.selections.showProductTags);
		},
		setPreviewProducts: (state, action) => {
			state.data.previewProducts = action.payload;
		},
		startApplying: (state) => {
			state.loadingStates.isApplyingProducts = true;
		},
		stopApplying: (state) => {
			state.loadingStates.isApplyingProducts = false;
		},
		onPreviewProductsReorder: (state, action) => {
			state.data.previewProducts = action.payload;
		},
		tagSearchLoading(
			state,
			action: PayloadAction<'category' | 'primary' | 'packaging' | 'general'>
		) {
			state.tagSearch[action.payload].loading = true;
		},
		tagSearchLoaded(
			state,
			action: PayloadAction<{
				tagType: 'category' | 'primary' | 'packaging' | 'general';
				options: TagOption[];
			}>
		) {
			state.tagSearch[action.payload.tagType].options = action.payload.options;
			state.tagSearch[action.payload.tagType].loading = false;
		},
		tagSearchError(
			state,
			action: PayloadAction<'category' | 'primary' | 'packaging' | 'general'>
		) {
			state.tagSearch[action.payload].loading = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchProductsByTags.pending, (state) => {
				state.loadingStates.isFetchingProducts = true;
				state.errors.fetchProductsError = null;
			})
			.addCase(fetchProductsByTags.fulfilled, (state, action) => {
				state.loadingStates.isFetchingProducts = false;
				state.loadingStates.productsHaveBeenFetched = true;

				const products = Array.isArray(action.payload) ? action.payload : [];
				const keySet = new Set(state.existingProductKeys);
				state.data.previewProducts = [
					...products.filter((p) =>
						keySet.has(`${p.corpSupplierCode}|${p.supplierProductNumber}`)
					),
					...products.filter(
						(p) =>
							!keySet.has(`${p.corpSupplierCode}|${p.supplierProductNumber}`)
					),
				];
			})
			.addCase(fetchProductsByTags.rejected, (state, action) => {
				state.loadingStates.isFetchingProducts = false;
				state.errors.fetchProductsError =
					(action.payload as string) || 'Failed to fetch products';
			})
			.addCase(applyTagsFromProduct.pending, (state) => {
				state.loadingStates.isApplying = true;
				state.errors.applyError = null;
			})
			.addCase(applyTagsFromProduct.fulfilled, (state) => {
				state.loadingStates.isApplying = false;
			})
			.addCase(applyTagsFromProduct.rejected, (state, action) => {
				state.loadingStates.isApplying = false;
				state.errors.applyError =
					action.error.message || 'Failed to apply tags from product';
			})
			.addCase(GetTagsFromProductNumber.pending, (state) => {
				state.loadingStates.isLoading = true;
				state.errors.loadError = null;
			})
			.addCase(GetTagsFromProductNumber.fulfilled, (state, action) => {
				state.loadingStates.isLoading = false;
				state.selections.productNumber = action.payload.productNumber;
				state.selections.selectedCategory = action.payload.selectedCategory;
				state.selections.selectedPrimary = action.payload.selectedPrimary;
				state.selections.selectedPackaging = action.payload.selectedPackaging;
				state.selections.selectedGeneral = action.payload.selectedGeneral;
			})
			.addCase(GetTagsFromProductNumber.rejected, (state, action) => {
				state.loadingStates.isLoading = false;
				state.errors.loadError =
					(action.payload as string) || 'Failed to load product tags';
			})
			.addCase(saveMetaProduct.fulfilled, (state) => {
				state.selections = {
					productNumber: '',
					selectedCategory: null,
					selectedPrimary: null,
					selectedPackaging: null,
					selectedGeneral: [],
					showProductTags: false,
				};
				state.data.previewProducts = [];
				state.currentMasterItemId = null;
				state.existingProductKeys = [];
				state.workingTagLinks = null;
				state.workingResolvedTags = null;
				state.loadingStates.productsHaveBeenFetched = false;
				state.loadingStates.isFetchingProducts = false;
				state.loadingStates.isApplying = false;
				state.tagSearch = {
					category: { options: [], loading: false },
					primary: { options: [], loading: false },
					packaging: { options: [], loading: false },
					general: { options: [], loading: false },
				};
			});
	},
});

export const {
	resetModal,
	setWorkingTags,
	openModalForMasterItem,
	updateProductNumber,
	updateSelectedCategory,
	updateSelectedPrimary,
	updateSelectedPackaging,
	updateSelectedGeneral,
	toggleShowProductTags,
	setPreviewProducts,
	startApplying,
	stopApplying,
	onPreviewProductsReorder,
} = tagsModalSlice.actions;

export default tagsModalSlice.reducer;
